// The page zoom. A pipe is laid out at its stylesheet's design width (`--md-container`)
// and the page wrapper (`body > .body-content` in a build, `body > #app` in the preview)
// is fitted to the screen with CSS `zoom: var(--app-zoom)` — down on narrow screens, and
// on touch devices up as well, the way the old `width=<design>` viewport meta scaled the
// whole page (desktop browsers ignore that meta, so desktop never scaled).
//
// A responsive pipe opts out of all of it: see pageLayout.ts and `applyAppZoom` below.
//
// `--app-zoom` is a plain number set on `<html>` by `applyAppZoom` below, never a CSS
// expression: a ratio of two lengths inside `zoom` is not portable — `100vw / 480px`
// needs typed arithmetic (Chrome 140, Safari 26), and older Safari evaluates
// `tan(atan2(100vw, 480px))` as if `100vw` were `100px`, shrinking the page to a fifth.
//
// Every iOS device, and any other touch engine whose `zoom` does not scale the whole
// design, falls back to the old meta instead (`data-page-scale="meta"` on `<html>`):
//  - before iOS 26, WebKit's `zoom` leaves `rem` lengths and font sizes at full size, and
//    every spacing and type token is `rem`, so text and padding came out ~23% too large;
//  - iOS 26 scales `rem`, but third-party payment SDKs that measure their box on screen
//    and write the size back — PayPal's buttons inside Primer — get the zoom twice, and
//    the meta is the only scaling that reaches them as it reaches control.
// The fallback re-points the viewport meta at the design width, keeps `--app-zoom` at 1,
// and adds `touch-action: manipulation` so the double-tap delay still goes. Desktop
// browsers ignore the meta, so they always take the zoom path.
//
// Three consequences for anything rendered inside that wrapper:
//  - Viewport units there are zoomed like every other length (standard zoom model), so a
//    screen-sized value is written `calc(100vh / var(--app-zoom, 1))`.
//  - Content teleported out to `<body>` leaves the zoom behind, and JS measuring across
//    that boundary converts with `effectiveZoom` / `viewportRect` below — never with a
//    `getBoundingClientRect().width / offsetWidth` ratio alone, which reads 1 on engines
//    still on the pre-standard zoom model.
//  - Width media queries read the real screen, where the old meta made every touch device
//    report the design width. A query meant for desktop layouts pairs its width with
//    `(hover: hover)` / `(pointer: fine)` so touch devices keep the design-width branch.

/** Media condition for a device the old `width=<design>` viewport meta scaled: touch-first. */
export const TOUCH_MEDIA = '(hover: none) and (pointer: coarse)'

/**
 * Sets `--app-zoom` on `<html>` to the layout width over `--md-container` — capped at 1
 * unless the device matches `TOUCH_MEDIA` (spelled out inline below) — and returns it.
 * A build runs it before first paint and on every resize (`APP_ZOOM_SCRIPT`); the
 * preview runs it whenever its stylesheet reaches the DOM.
 *
 * On its first run with a design width on a touch device, in a top-level page, it also
 * picks the scaling path (see the header) and records it as `data-page-scale`: `meta` on
 * iOS (`-webkit-touch-callout` exists only in iOS WebKit, which every iOS browser runs),
 * otherwise `zoom` if `zoom` is supported and scales a `rem` box and a `rem` font exactly
 * like their `px` twins, `meta` if not. Both twins are measured the same way, so the
 * check holds whichever way an engine reports sizes under `zoom`.
 *
 * A responsive page (`data-layout="responsive"`, pageLayout.ts) is not scaled at all, on
 * any screen: `--app-zoom` stays 1 and nothing falls back to the meta. The attribute name
 * is spelled out because this function cannot import pageLayout.ts; pageZoom.test.ts pins
 * it to LAYOUT_ATTR.
 *
 * Its own source text is that inline script, so it must stay self-contained: no imports,
 * no module-level references, nothing a transpiler would rewrite into a helper call.
 */
export function applyAppZoom(): number {
  const root = document.documentElement
  if (root.getAttribute('data-layout') === 'responsive') {
    root.style.setProperty('--app-zoom', '1')
    return 1
  }
  const rootStyle = getComputedStyle(root)
  const design = rootStyle.getPropertyValue('--md-container').trim()
  let designPx = parseFloat(design)
  if (/r?em$/.test(design)) designPx *= parseFloat(rootStyle.fontSize) || 16
  const touch = matchMedia('(hover: none) and (pointer: coarse)').matches

  // Only a top-level page can fall back: an iframe ignores the viewport meta.
  let mode = root.getAttribute('data-page-scale')
  if (!mode && touch && designPx > 0 && document.body && window.self === window.top) {
    const css = typeof CSS !== 'undefined' ? CSS : undefined
    const ios = !!css && css.supports('-webkit-touch-callout', 'none')
    let zoomScalesAll = !ios && !!css && css.supports('zoom', '0.5')
    if (zoomScalesAll) {
      const tenRem = (parseFloat(rootStyle.fontSize) || 16) * 10
      const probe = document.createElement('div')
      probe.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;zoom:0.5'
      probe.innerHTML =
        '<div style="width:10rem"></div>' +
        `<div style="width:${tenRem}px"></div>` +
        '<div style="font-size:10rem;line-height:1">x</div>' +
        `<div style="font-size:${tenRem}px;line-height:1">x</div>`
      document.body.appendChild(probe)
      const box = probe.children
      const remBox = (box[0] as HTMLElement).offsetWidth
      const pxBox = (box[1] as HTMLElement).offsetWidth
      const remFont = (box[2] as HTMLElement).offsetHeight
      const pxFont = (box[3] as HTMLElement).offsetHeight
      zoomScalesAll = remBox < pxBox * 1.25 && remFont < pxFont * 1.25
      probe.remove()
    }
    mode = zoomScalesAll ? 'zoom' : 'meta'
    if (mode === 'meta') {
      const viewport = document.querySelector('meta[name="viewport"]')
      if (viewport)
        viewport.setAttribute('content', `width=${Math.round(designPx)}, maximum-scale=5.0`)
      root.style.setProperty('touch-action', 'manipulation')
    }
    root.setAttribute('data-page-scale', mode)
  }

  const width = root.clientWidth
  const fit = width / designPx
  const zoom = mode === 'meta' || !(designPx > 0 && width > 0) ? 1 : touch ? fit : Math.min(fit, 1)
  root.style.setProperty('--app-zoom', String(zoom))
  return zoom
}

/**
 * Inline `<body>`-open script for a build: the stylesheets in `<head>` are parsed by then
 * and none of the page is, so the first paint is already zoomed.
 */
export const APP_ZOOM_SCRIPT = `(function(apply){apply();addEventListener('resize',function(){apply()})})(${applyAppZoom.toString()})`

/** Zoom `el` renders under: its own computed `zoom` times every ancestor's below `<html>`. */
export function effectiveZoom(el: Element | null): number {
  if (el && 'currentCSSZoom' in el && typeof el.currentCSSZoom === 'number') {
    return el.currentCSSZoom || 1
  }
  // `<html>` is left out: engines without `currentCSSZoom` (Chrome before 128) report the
  // browser's page zoom as its `zoom`, which their rects and offsets have already divided out.
  let zoom = 1
  for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
    const own = parseFloat(getComputedStyle(node).getPropertyValue('zoom'))
    if (own > 0) zoom *= own
  }
  return zoom
}

let rectsInOwnSpace: boolean | undefined

/**
 * Whether `getBoundingClientRect()` reports a zoomed element in its own unzoomed px rather
 * than viewport px — the pre-standard zoom model (Chrome before 128, older WebKit). Measured
 * once with a probe instead of sniffed, so an engine is judged by what it does.
 */
function clientRectsIgnoreZoom(): boolean {
  if (rectsInOwnSpace === undefined) {
    const probe = document.createElement('div')
    probe.style.cssText = 'position:absolute;visibility:hidden;width:100px;zoom:0.5'
    document.body.appendChild(probe)
    rectsInOwnSpace = probe.getBoundingClientRect().width > 75
    probe.remove()
  }
  return rectsInOwnSpace
}

export interface ViewportRect {
  top: number
  left: number
  width: number
  height: number
}

/** `el.getBoundingClientRect()` in viewport px on both zoom models. */
export function viewportRect(el: Element): ViewportRect {
  const { top, left, width, height } = el.getBoundingClientRect()
  const scale = clientRectsIgnoreZoom() ? effectiveZoom(el) : 1
  return { top: top * scale, left: left * scale, width: width * scale, height: height * scale }
}

/**
 * Prepends a `TOUCH_MEDIA` rule to a generated `sizes` list, pinned to the size the list
 * resolves to at `layoutWidth` — the width the old viewport meta laid a touch device out
 * at. So a touch device downloads the same file it did under that meta, whatever its
 * real width; everything else keeps the list as it was. `sizes` is Nuxt Image's output:
 * `(max-width: Npx) SIZE` entries, then one bare fallback, SIZE in `px` or `vw`. Anything
 * else is returned untouched.
 */
export function withTouchLayoutSizes(sizes: string | undefined, layoutWidth: number) {
  if (!sizes || !(layoutWidth > 0)) return sizes
  for (const entry of sizes.split(', ')) {
    const match = /^(?:\(max-width:\s*(\d+)px\)\s+)?(\d+(?:\.\d+)?)(px|vw)$/.exec(entry.trim())
    if (!match) return sizes
    const [, maxWidth, value, unit] = match
    if (maxWidth !== undefined && Number(maxWidth) < layoutWidth) continue
    const px = unit === 'vw' ? (layoutWidth * Number(value)) / 100 : Number(value)
    return `${TOUCH_MEDIA} ${Math.round(px)}px, ${sizes}`
  }
  return sizes
}
